import { VIEW_WIDTH, VIEW_HEIGHT } from './events';
import { tileSourceRect } from './tileSheet';

/**
 * Renders a tile layer from the map the server sent on join.
 *
 * ONLY DRAWS WHAT THE CAMERA CAN SEE. A 200x200 map is 40,000 tiles per layer
 * and three layers; drawing all of them every frame is 120,000 drawImage calls
 * at 60fps. Culling to the ~20x11 visible window makes it ~700 instead, and it
 * is the difference between this scaling to real map sizes and not.
 */
export default class TileMap {
  constructor(map, tilesetSrc) {
    this.map = map;
    this.tileSize = map.tileSize;
    this.columns = map.tileset.columns;
    this.firstgid = map.tileset.firstgid ?? 1;

    // name -> data array, so draw() takes a layer name rather than an index.
    this.layers = new Map(map.layers.map((l) => [l.name, l.data]));

    this.isLoaded = false;
    this.image = new Image();
    this.image.onload = () => { this.isLoaded = true; };
    this.image.onerror = () => {
      // eslint-disable-next-line no-console
      console.warn(`[TileMap] failed to load tileset "${tilesetSrc}"`);
    };
    this.image.src = tilesetSrc;
  }

  get widthPx() {
    return this.map.width * this.tileSize;
  }

  get heightPx() {
    return this.map.height * this.tileSize;
  }

  /**
   * Draw one named layer through the camera.
   * Unknown layer names are ignored rather than throwing — a map missing an
   * optional layer should render, not crash the loop.
   */
  draw(ctx, layerName, camera) {
    if (!this.isLoaded) return;

    const data = this.layers.get(layerName);
    if (!data) return;

    const size = this.tileSize;

    // Visible tile window, padded by one so tiles straddling the edge still
    // draw rather than popping in.
    const startX = Math.max(0, Math.floor(camera.x / size));
    const startY = Math.max(0, Math.floor(camera.y / size));
    const endX = Math.min(this.map.width - 1, Math.floor((camera.x + VIEW_WIDTH) / size) + 1);
    const endY = Math.min(this.map.height - 1, Math.floor((camera.y + VIEW_HEIGHT) / size) + 1);

    for (let ty = startY; ty <= endY; ty += 1) {
      for (let tx = startX; tx <= endX; tx += 1) {
        const gid = data[ty * this.map.width + tx];
        const src = tileSourceRect(gid, {
          columns: this.columns, firstgid: this.firstgid, tileSize: size,
        });
        if (!src) continue; // eslint-disable-line no-continue

        ctx.drawImage(
          this.image,
          src.sx,
          src.sy,
          size,
          size,
          camera.toScreenX(tx * size),
          camera.toScreenY(ty * size),
          size,
          size,
        );
      }
    }
  }

  /**
   * The walls layer, shaped for the pathfinder.
   *
   * `walls` is the same layer the SERVER derives collision from, so a route
   * planned against it is planned against the real geometry rather than a
   * separate idea of it.
   */
  wallGrid() {
    return {
      walls: this.layers.get('walls'),
      width: this.map.width,
      height: this.map.height,
    };
  }
}
